import React, { CSSProperties, useCallback, useContext } from 'react';
import { EadSimple, EadTree } from '../../context/ead-context';
import { Link } from 'react-router-dom';
import { getNodeId } from 'utils/ead-utils';
import { useStyles } from './ead-list-item-styles';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import { Button, Tooltip } from '@material-ui/core';
import {
  LevelDefaultIcon,
  levelIcon,
} from 'components/grid/grid-hooks/breadcrumbs-hook';
import { fetchEadChildren } from 'components/grid/grid-api';
import { HierarchyContext } from '../../context/hierarchy-context';

type Props = {
  index: number;
  style: CSSProperties;
  data: EadSimple;
};

const getLabel = (ead: EadSimple) => {
  return (
    ead?.title ??
    //@ts-ignore
    ead?.archivalAidMetadata?.name ??
    //@ts-ignore
    ead?.label ??
    //@ts-ignore
    ead?.referenceId ??
    //@ts-ignore
    ead?.id
  );
};

function EadListItem({ data, index, style }: Props) {
  const { addChildren, expanded, removeChildren, setLoading } = useContext(
    EadTree
  );

  const { getRootId, eadId, rootUrl } = useContext(HierarchyContext);
  const classes = useStyles();
  const nodeId = getNodeId(data) ?? '';
  const Icon = data.level
    ? levelIcon?.[data.level] ?? LevelDefaultIcon
    : LevelDefaultIcon;

  // eadId === data.id =>  check also regular id so the root archival aid is detected when active and has baseIdentifier
  const isActive = eadId === nodeId || eadId === data.id;
  const isExpanded = expanded?.[nodeId];

  const handleExpand = useCallback(async () => {
    setLoading(true);
    const nodeWithChildren = await fetchEadChildren([nodeId]);
    addChildren?.((nodeWithChildren as unknown) as EadSimple[]);
    setLoading(false);
  }, [addChildren, nodeId, setLoading]);

  const handleCollapse = useCallback(async () => {
    removeChildren?.(data);
  }, [data, removeChildren]);

  return (
    <div key={data.id} style={style}>
      <div
        className={classes.wrapper}
        style={{
          marginLeft: 20 * data.nlevel + 2,
          cursor: 'pointer',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        {index > 0 && data.childrenCount > 0 ? (
          <Button
            onClick={isExpanded ? handleCollapse : handleExpand}
            variant="outlined"
            className={classes.expandButton}
          >
            {isExpanded ? (
              <div className={classes.buttonIcon}>
                <RemoveIcon fontSize="small" />
              </div>
            ) : (
              <div className={classes.buttonIcon}>
                <AddIcon fontSize="small" />
              </div>
            )}
          </Button>
        ) : (
          <>{index > 0 && <span className={classes.placeholder} />}</>
        )}

        <span className={classes.iconWrapper}>
          <Tooltip
            title={<span className={classes.tooltip}>{data.levelLabel}</span>}
            placement="top"
          >
            <Icon className={classes.icon} />
          </Tooltip>
        </span>
        <span
          className={`${classes.eadItem} ${isActive ? classes.active : ''}`}
        >
          <Link
            className={`${classes.link} ${isActive ? classes.active : ''}`}
            to={`${rootUrl}/${getRootId?.()}/ead/${nodeId}`}
          >
            {getLabel(data)}
          </Link>
        </span>
      </div>
    </div>
  );
}

export default EadListItem;
